/**
 * The one file viewer for the whole app. Any component calls
 * `openFile({ path, name })` with an auth-checked API path; the single
 * `<FileViewerHost />` (mounted in App) fetches it with the session token,
 * shows progress, then previews it. Office files are asked for as PDF
 * (`?preview=1`, converted on the server); Download always saves the original.
 * Spec: `homework-files-docs/02-UNIVERSAL-FILE-VIEWER.md`.
 */

import { useSyncExternalStore } from "react";

import { api } from "@/lib/api/client";
import { ApiError } from "@/lib/api/errors";

import { fileKind, isOfficeName, type FileKind } from "./fileKinds";

export interface OpenFileRequest {
  /** API-relative path, for example `/homework/1/attachments/3/download/`. */
  path: string;
  name: string;
  /** Optional extra action in the viewer bar, such as "Open in Google Drive". */
  external?: { label: string; href: string };
}

export interface LoadedFile {
  kind: FileKind;
  blob: Blob;
  objectUrl: string;
  /** True when the server sent a PDF made from an Office original. */
  converted: boolean;
  /** An Office file the server could not convert: offer the download. */
  officeUnavailable: boolean;
}

export interface ViewerState {
  open: boolean;
  request: OpenFileRequest | null;
  status: "idle" | "loading" | "ready" | "error";
  loaded: number;
  total: number | null;
  file: LoadedFile | null;
  error: string | null;
}

const IDLE: ViewerState = {
  open: false,
  request: null,
  status: "idle",
  loaded: 0,
  total: null,
  file: null,
  error: null,
};

let state: ViewerState = IDLE;
const listeners = new Set<() => void>();
let controller: AbortController | null = null;
let releaseTimer: number | undefined;

function set(patch: Partial<ViewerState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useViewerState(): ViewerState {
  return useSyncExternalStore(subscribe, () => state, () => IDLE);
}

function withPreview(path: string): string {
  return `${path}${path.includes("?") ? "&" : "?"}preview=1`;
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
    return "This file isn't available to you, or it has been removed.";
  }
  if (err instanceof ApiError && err.status === 502) {
    return "Google Drive didn't answer. Give it another go in a moment.";
  }
  return "This file couldn't load. Check your connection and try again.";
}

function release(file: LoadedFile | null): void {
  if (file) URL.revokeObjectURL(file.objectUrl);
}

export async function openFile(request: OpenFileRequest): Promise<void> {
  controller?.abort();
  window.clearTimeout(releaseTimer);
  release(state.file);
  const mine = new AbortController();
  controller = mine;
  set({ ...IDLE, open: true, request, status: "loading" });
  const office = isOfficeName(request.name);
  // Start the PDF engine while the bytes download.
  if (office || /\.pdf$/i.test(request.name)) void import("./pdf").then((m) => m.warmPdf()).catch(() => undefined);
  try {
    const blob = await api.file(office ? withPreview(request.path) : request.path, {
      signal: mine.signal,
      onProgress: (loaded, total) => {
        if (controller === mine) set({ loaded, total });
      },
    });
    if (controller !== mine) return;
    const converted = office && blob.type === "application/pdf";
    const kind = converted ? "pdf" : fileKind(blob.type, request.name);
    set({
      status: "ready",
      file: {
        kind: office && !converted ? "other" : kind,
        blob,
        objectUrl: URL.createObjectURL(blob),
        converted,
        officeUnavailable: office && !converted,
      },
    });
  } catch (err) {
    if (controller !== mine || mine.signal.aborted) return;
    set({ status: "error", error: friendlyError(err) });
  }
}

export function retryFile(): void {
  if (state.request) void openFile(state.request);
}

export function closeViewer(): void {
  controller?.abort();
  controller = null;
  const file = state.file;
  set({ open: false });
  // Keep the content mounted through the exit animation, then free it.
  releaseTimer = window.setTimeout(() => {
    if (state.open) return;
    release(file);
    state = IDLE;
    listeners.forEach((l) => l());
  }, 400);
}

function save(href: string, name: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Save the original file (fetching it when the preview is a converted PDF). */
export async function downloadCurrent(): Promise<void> {
  const { request, file } = state;
  if (!request) return;
  if (file && !file.converted) {
    save(file.objectUrl, request.name);
    return;
  }
  const blob = await api.blob(request.path);
  const url = URL.createObjectURL(blob);
  save(url, request.name);
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Fetch a protected file and save it straight away, no viewer. */
export async function downloadFile(path: string, name: string): Promise<void> {
  const blob = await api.blob(path);
  const url = URL.createObjectURL(blob);
  save(url, name);
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
